/* eslint-disable no-case-declarations */
import { type Component, defineComponent, h, markRaw, onMounted } from 'vue'

import type { IconName } from '@backmarket/design-system/icons/icons'
import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import type { ProductQuery } from '@backmarket/http-api/src/api-specs-content/models/product-query'
import RecommendationAlgoliaCarousel, {
  type RecommendationAlgoliaCarouselProps,
} from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import type { SortAlgoliaHitsFn } from '../../products/models/sort-alglia-hits-fn'
import type { BlockRendererProps } from '../../shared-components/BlockRenderer/BlockRenderer.types'
import BlockRenderer from '../../shared-components/BlockRenderer/BlockRenderer.vue'
import { useIcon } from '../../shared-components/DynamicIcon/helpers/useIcon'
import RefinedProducts, {
  type RefinedProductsBlockProps,
} from '../RefinedProducts/RefinedProducts.vue'

export type BrazeContentCardExtras = {
  feed?: string
  type?: string
  title?: string
  product_granularity?: 'variant' | 'backbox' | 'parent'
  product_id?: string
  filters?: string
  priority?: string
  icon?: string
  recommendation_type?: string
  content_card_family?: string
} & Record<string, string>

export type BrazeContentCard = {
  expiresAt: Date | null
  id?: string
  extras: BrazeContentCardExtras
  isControl: boolean
  pinned: boolean
  updated: Date | null
  viewed: boolean
}

// Define supported ContentCard types in one place for easy extension
// When adding a new type, add it here and add a corresponding case in mapPropsByBlockType
export type SupportedContentCardType =
  | RecommendationAlgoliaCarouselProps
  | RefinedProductsBlockProps
export type SupportedFallbackTypes = BlockRendererProps

export type ContentCard = {
  id: string
  title: string
  icon?: Component
  tag: Component
  props: SupportedContentCardType | SupportedFallbackTypes
  productIds: string[]
}

function sortByPriorityFn(a: BrazeContentCard, b: BrazeContentCard): number {
  const priorityA = a.extras.priority
    ? parseInt(a.extras.priority, 10)
    : Infinity

  const safePriorityA = Number.isNaN(priorityA) ? Infinity : priorityA

  const priorityB = b.extras.priority
    ? parseInt(b.extras.priority, 10)
    : Infinity

  const safePriorityB = Number.isNaN(priorityB) ? Infinity : priorityB

  return safePriorityA - safePriorityB
}
function mapPropsByBlockType({
  type,
  brazeContentCardExtras,
  productQuery,
  blockPosition,
  index,
}: {
  type: string
  brazeContentCardExtras: BrazeContentCardExtras
  productQuery: PersonalizedFeedApiProps['productQuery']
  blockPosition: number
  index: number
}):
  | { title: string; tag: Component; props: SupportedContentCardType }
  | undefined {
  const logger = useLogger()

  if (!brazeContentCardExtras.title) {
    logger.error(
      `[PERSONALIZED_CONTENT_CARD] Missing title for content card type: ${type}`,
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }

  switch (type) {
    case 'recommendations':
      return buildRecommendationAlgoliaCarouselProps(
        brazeContentCardExtras,
        logger,
        { blockPosition, index },
      )

    case 'search':
      return buildRefinedProductsProps(brazeContentCardExtras, productQuery, {
        blockPosition,
        index,
      })

    default:
      logger.error(
        `[PERSONALIZED_CONTENT_CARD] Unsupported content card type: ${type}`,
        {
          owners: ['bot-squad-content-front'],
        },
      )

      return undefined
  }
}
// objectID is the id of a backbox product (syntax: <product-id>_<grade>_<special-offer-type>)
// id is the id of a product ("variant")
function getIdFacetFromGranularity(
  granularity: 'variant' | 'backbox' | 'parent' | string,
): 'id' | 'objectID' {
  return ['variant', 'parent'].includes(granularity) ? 'id' : 'objectID'
}
function buildIdFacetFilters(brazeContentCard: BrazeContentCardExtras) {
  if (!brazeContentCard.product_id) {
    return undefined
  }

  const idFacet = getIdFacetFromGranularity(
    brazeContentCard.product_granularity || '',
  )

  const filters = brazeContentCard.product_id
    ?.trim()
    ?.split(',')
    ?.map((id) => `${idFacet}:${id}`)
    ?.join(' OR ')

  return filters ? `(${filters})` : undefined
}

function buildFilters(brazeContentCard: BrazeContentCardExtras) {
  const filters = buildIdFacetFilters(brazeContentCard)

  if (!filters && brazeContentCard.filters) {
    return brazeContentCard.filters
  }

  if (brazeContentCard.filters) {
    return `${filters} AND ${brazeContentCard.filters}`
  }

  return filters
}

function mergeAPIAndBrazeProductQuery(
  brazeContentCard: BrazeContentCardExtras,
  apiProductQueryByGranularity: PersonalizedFeedApiProps['productQuery'],
): ProductQuery {
  const productQuery =
    apiProductQueryByGranularity[
      brazeContentCard.product_granularity as keyof typeof apiProductQueryByGranularity
    ]

  if (!productQuery) {
    return {
      apiKey: '',
      indexName: '',
      params: {},
    }
  }

  return {
    ...productQuery,
    params: {
      ...productQuery.params,
      filters: buildFilters(brazeContentCard),
    },
  }
}
function buildRefinedProductsProps(
  brazeContentCardExtras: BrazeContentCardExtras,
  productQuery: PersonalizedFeedApiProps['productQuery'],
  { blockPosition, index }: { blockPosition: number; index: number },
) {
  const finalProductQuery = mergeAPIAndBrazeProductQuery(
    brazeContentCardExtras,
    productQuery,
  )

  const productIds = brazeContentCardExtras.product_id?.split(',') || []

  const idFacet: 'id' | 'objectID' = getIdFacetFromGranularity(
    brazeContentCardExtras.product_granularity || '',
  )
  const sortAlgoliaHitsFn: SortAlgoliaHitsFn = (productA, productB) => {
    const indexA =
      productIds.indexOf(productA[idFacet]) ?? Number.MAX_SAFE_INTEGER
    const indexB =
      productIds.indexOf(productB[idFacet]) ?? Number.MAX_SAFE_INTEGER

    return indexA - indexB
  }

  const refinedProductBlockProps: RefinedProductsBlockProps = {
    productLayout: 'slider',
    productQuery: finalProductQuery,
    clientOnly: true,
    blockPosition,
    tracking: {
      productRecoComponentPosition: index + 1,
      productComponent: 'PersonalizedFeed_RefinedProducts',
      personalizationContentCardFamily:
        brazeContentCardExtras.content_card_family || '',
    },
    // if there is no product_id, we don't need to sort the Algolia hits
    sortFn: productIds.length > 0 ? sortAlgoliaHitsFn : undefined,
    productGranularity: brazeContentCardExtras.product_granularity,
  }

  return {
    title: brazeContentCardExtras.title || '',
    tag: markRaw(RefinedProducts),
    props: refinedProductBlockProps,
  }
}

function buildRecommendationAlgoliaCarouselProps(
  brazeContentCardProps: BrazeContentCardExtras,
  logger: ReturnType<typeof useLogger>,
  { blockPosition, index }: { blockPosition: number; index: number },
) {
  if (!brazeContentCardProps.product_id) {
    logger.error(
      '[PERSONALIZED_CONTENT_CARD] Missing product_id for content card type: recommendations',
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }
  if (
    !brazeContentCardProps.recommendation_type ||
    !['bought-together', 'related-products'].includes(
      brazeContentCardProps.recommendation_type,
    )
  ) {
    logger.error(
      '[PERSONALIZED_CONTENT_CARD] Unsupported recommendation_type for content card type: recommendations',
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }

  const trackingData = {
    productRecoComponentPosition: index + 1,
    productComponent: 'PersonalizedFeed_RecommendationAlgoliaCarousel',
    personalizationContentCardFamily:
      brazeContentCardProps.content_card_family || '',
  }

  const isCrossSell =
    brazeContentCardProps.recommendation_type === 'bought-together'
  const isRelatedProducts =
    brazeContentCardProps.recommendation_type === 'related-products'

  const props: RecommendationAlgoliaCarouselProps = {
    cache: true,
    clientOnly: true,
    recommendationQuery: {
      objectIds: [brazeContentCardProps.product_id],
      model:
        brazeContentCardProps.recommendation_type as RecommendationAlgoliaCarouselProps['recommendationQuery']['model'],
      options: {
        queryParameters: {
          filters: brazeContentCardProps.filters,
        },
      },
    },
    blockPosition,
    trackingData: {
      ...trackingData,
      ...(isCrossSell && { zone: 'homepage_cross_sell_reco' }),
      ...(isRelatedProducts && {
        zone: 'homepage_reco',
        recoTab: brazeContentCardProps.content_card_family || undefined,
      }),
    },
    ...(isCrossSell && { blockZone: 'homepage_cross_sell_reco' }),
    ...(isRelatedProducts && { blockZone: 'homepage_reco' }),
    productGranularity: brazeContentCardProps.product_granularity,
  }

  return {
    title: brazeContentCardProps.title || '',
    tag: markRaw(RecommendationAlgoliaCarousel),
    props,
  }
}

/**
 * Returns a component that renders the design-system icon for the given name.
 */
function createIconComponent(iconName: string): Component {
  const name = iconName as IconName

  return defineComponent({
    name: 'PersonalizedFeedIcon',
    setup() {
      const { iconComponent, fetchIconComponent } = useIcon(() => name)

      if (import.meta.client) {
        onMounted(() => {
          void fetchIconComponent()
        })
      }

      return () => (iconComponent.value ? h(iconComponent.value) : null)
    },
  })
}

function mapContentCard(
  card: BrazeContentCard,
  productQuery: PersonalizedFeedApiProps['productQuery'],
  { blockPosition, index }: { blockPosition: number; index: number },
): ContentCard | undefined {
  const { icon: iconName, type = '' } = card.extras

  const blockPropsResult = mapPropsByBlockType({
    type,
    brazeContentCardExtras: card.extras,
    productQuery,
    blockPosition,
    index,
  })

  if (!blockPropsResult) {
    return undefined
  }

  return {
    // The same content card with the same id can be received multiple times, so we need to generate a unique id for each occurrence
    id: crypto.randomUUID(),
    icon: iconName ? markRaw(createIconComponent(iconName)) : undefined,
    title: blockPropsResult.title,
    tag: blockPropsResult.tag,
    props: blockPropsResult.props,
    productIds: card.extras.product_id?.split(',') || [],
  }
}

const dedupeCardsByCampaignId = (
  card: BrazeContentCard,
  index: number,
  arr: BrazeContentCard[],
): boolean => {
  // if the card has no campaign_id, we keep it
  if (!card.extras.campaign_id) {
    return true
  }

  // if the card has a campaign_id, we check if it is the first occurrence of the campaign_id
  return (
    index ===
    arr.findIndex((c) => c.extras.campaign_id === card.extras.campaign_id)
  )
}
export function transformContentCards({
  cards,
  feed,
  productQuery,
  blockPosition,
}: {
  cards: BrazeContentCard[]
  feed?: string
  productQuery: PersonalizedFeedApiProps['productQuery']
  blockPosition: number
}): ContentCard[] {
  return cards
    .filter(({ extras }) => extras.feed === feed)
    .filter(dedupeCardsByCampaignId)
    .sort(sortByPriorityFn)
    .map((card, index) =>
      mapContentCard(card, productQuery, { blockPosition, index }),
    )
    .filter((card) => !isEmpty(card))
}

export function transformFallbackBlocks({
  fallback,
  blockPosition,
  title: blockTitle,
}: {
  fallback: PersonalizedFeedApiProps['fallback']
  blockPosition: number
  title?: PersonalizedFeedApiProps['title']
}): ContentCard[] {
  return fallback.map((block, index) => {
    const { title, subtitle, ctaLink, ...remainingProps } = block.props

    return {
      id: block.id,
      tag: markRaw(BlockRenderer),
      title: title || blockTitle || '',
      props: {
        ...block,
        blockPosition,
        hydrationMode: 'eager',
        props: remainingProps,
        tracking: {
          productRecoComponentPosition: index + 1,
          productComponent: `PersonalizedFeed_${block.type}_fallback`,
          personalizationContentCardFamily: 'fallback',
        },
      },
      productIds: [],
    }
  })
}
